import api from './api';

export type TargetPlatform =
  | 'youtube_shorts'
  | 'instagram_reels'
  | 'tiktok'
  | 'facebook_reels'
  | 'linkedin_video'
  | 'twitter_video';

export interface ClipGenerationOptions {
  silenceThresholdDb?: number;
  minClipLength?: number;
  maxClipLength?: number;
  outputCounts?: Record<TargetPlatform, number>;
  faceTracking?: boolean;
  autoZoom?: boolean;
  autoSubtitles?: boolean;
  animatedCaptions?: boolean;
  noiseReduction?: boolean;
  musicRecommendation?: boolean;
  detectKeywords?: string[];
  emotionDetection?: boolean;
}

export interface ManualSegment {
  start: number;
  end: number;
  title?: string;
}

export interface ClipGenerationRequest {
  title?: string;
  videoS3Key?: string;
  videoS3Url?: string;
  audioS3Key?: string;
  audioS3Url?: string;
  transcript?: string;
  language?: string;
  targetPlatforms: TargetPlatform[];
  projectId?: string;
  // User-specified clip windows (seconds into the source video), bypassing auto
  // highlight detection. platformSegments overrides manualSegments per platform.
  manualSegments?: ManualSegment[];
  platformSegments?: Partial<Record<TargetPlatform, ManualSegment[]>>;
  options?: ClipGenerationOptions;
}

export interface IHighlight {
  start: number;
  end: number;
  score: number;
  type: string;
  label?: string;
  text?: string;
  emotion?: {
    primary: string;
    secondary?: string;
    intensity: number;
    viralityReason?: string;
    source: 'llm' | 'keyword_fallback';
  };
}

export interface IGeneratedClip {
  clipId: string;
  platform: TargetPlatform;
  aspectRatio: string;
  length: number;
  startTime: number;
  endTime: number;
  title?: string;
  description?: string;
  hashtags?: string[];
  s3Key?: string;
  s3Url?: string;
  thumbnailS3Url?: string;
  thumbnail?: {
    selectedAt: number;
    intent: string;
    intentConfidence: number;
    score: number;
    strategy: 'intent_ranked' | 'quality_fallback';
    generatedAt: string;
  };
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  engagementScore?: number;
  subtitles?: {
    srtS3Key?: string;
    srtS3Url?: string;
    srtContent?: string;
    burned?: boolean;
    generatedAt?: string;
  };
  music?: {
    mood: string;
    recommendedGenres: string[];
    tracks: Array<{ title: string; artist?: string; genre: string; reason: string }>;
    recommendedAt: string;
    appliedTrackUrl?: string;
    applied?: boolean;
  };
}

export interface IClipGenerationJob {
  title?: string;
  jobId: string;
  videoS3Url?: string;
  audioS3Url?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  currentStep: string;
  error?: string;
  targetPlatforms: TargetPlatform[];
  highlights: IHighlight[];
  clips: IGeneratedClip[];
  createdAt: string;
  updatedAt: string;
}

const clipGenerationApi = {
  createJob: (data: ClipGenerationRequest) =>
    api.post<{ success: boolean; jobId: string; job: Partial<IClipGenerationJob> }>('/clip-generation', data),

  getJobs: (projectId?: string) =>
    api.get<{ success: boolean; jobs: Partial<IClipGenerationJob>[] }>('/clip-generation', {
      params: projectId ? { projectId } : undefined,
    }),

  getJob: (jobId: string) =>
    api.get<{ success: boolean; job: IClipGenerationJob }>(`/clip-generation/${jobId}`),

  getClipSignedUrl: (jobId: string, clipId: string) =>
    api.get<{ success: boolean; url: string }>(`/clip-generation/${jobId}/clips/${clipId}/url`),

  getThumbnailSignedUrl: (jobId: string, clipId: string) =>
    api.get<{ success: boolean; url: string }>(`/clip-generation/${jobId}/clips/${clipId}/thumbnail-url`),

  getSubtitleSignedUrl: (jobId: string, clipId: string) =>
    api.get<{ success: boolean; url: string }>(`/clip-generation/${jobId}/clips/${clipId}/subtitle-url`),

  deleteClip: (jobId: string, clipId: string) =>
    api.delete<{ success: boolean; message: string }>(`/clip-generation/${jobId}/clips/${clipId}`),

  updateSubtitles: (jobId: string, clipId: string, srtContent: string, burn = false) =>
    api.patch<{ success: boolean; clip: IGeneratedClip }>(`/clip-generation/${jobId}/clips/${clipId}/subtitles`, {
      srtContent,
      burn,
    }),

  updateTrim: (jobId: string, clipId: string, startTime: number, endTime: number, title?: string) =>
    api.patch<{ success: boolean; clip: IGeneratedClip }>(`/clip-generation/${jobId}/clips/${clipId}/trim`, {
      startTime,
      endTime,
      title,
    }),

  updateMusic: (
    jobId: string,
    clipId: string,
    mood: string,
    tracks?: { title: string; artist?: string; genre: string; reason: string }[],
    musicTrackUrl?: string
  ) =>
    api.patch<{ success: boolean; clip: IGeneratedClip }>(`/clip-generation/${jobId}/clips/${clipId}/music`, {
      mood,
      tracks,
      musicTrackUrl,
    }),

  cancelJob: (jobId: string) =>
    api.delete<{ success: boolean; message: string }>(`/clip-generation/${jobId}`),

  retryJob: (jobId: string) =>
    api.post<{ success: boolean; message: string }>(`/clip-generation/${jobId}/retry`),
};

export default clipGenerationApi;
